// Paketler sayfası taslak modeli (DR4) — saf. Taslak sunucu kalemlerine (`base`) göre İŞLEM olarak
// tutulur: `added` (sıralı), `removed`, `defaults` (üye başına birim/aktif formül/değişim). Böylece
// 409 KPI_PACKAGE_STALE sonrası "Yeniden yükle" taslağı YENİ kalemlere yeniden uygular
// (`rebaseDraft`): zaten yapılmış ekleme/çıkarma sessizce düşer, artık uygulanamayan işlem
// (kullanımdan kalkmış/silinmiş gösterge, artık pakette olmayan kalemin varsayılanı) çıkarılır ve
// kullanıcıya listelenir. Kırılım kuralı (Rev 4 §3 / backend `applyPackageItems` orphan kapısı):
// üye → grubu + toplamı birlikte girer; toplam/grup çıkınca altı da çıkar; üyesiz kalan grup düşer.

import {
  isKpiCatalogCategory,
  kpiCatalogChildCounts,
  parseFormula,
  type KpiMetric,
  type KpiPackageItem,
  type KpiPackageItemInput,
} from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "./kpi-packages-copy";

export type DraftMetric = Pick<
  KpiMetric,
  | "id"
  | "code"
  | "label"
  | "parentId"
  | "clientId"
  | "status"
  | "nodeKind"
  | "dimensionMember"
  | "defaultFormulas"
  | "isDerived"
>;

export interface PackageDefaults {
  unitDefault: string | null;
  activeFormulaId: string | null;
  showChange: boolean | null;
}

/** Taslakta EKLENEN kalemin kaynağı (sunucuya `source/sourceReason` olarak gider). */
export interface PackageSource {
  source: "manual" | "ai";
  reason: string | null;
}

export interface PackagePick extends PackageSource {
  metricId: string;
}

export interface PackageDraft {
  added: readonly string[];
  /** yalnız `added` içindeki kalemler için; tabandaki kalemin kaynağı değişmez */
  sources: ReadonlyMap<string, PackageSource>;
  removed: ReadonlySet<string>;
  defaults: ReadonlyMap<string, PackageDefaults>;
}

export interface DraftContext {
  byId: ReadonlyMap<string, DraftMetric>;
  kids: ReadonlyMap<string, readonly DraftMetric[]>;
  byCode: ReadonlyMap<string, DraftMetric>;
  categories: ReadonlySet<string>;
}

export function draftContext(metrics: readonly DraftMetric[]): DraftContext {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const kids = new Map<string, DraftMetric[]>();
  for (const m of metrics) {
    if (!m.parentId) continue;
    const list = kids.get(m.parentId) ?? [];
    list.push(m);
    kids.set(m.parentId, list);
  }
  const counts = kpiCatalogChildCounts(metrics);
  const categories = new Set(
    metrics.filter((m) => isKpiCatalogCategory(m, counts.get(m.id) ?? 0)).map((m) => m.id),
  );
  // kod tekil; yine de global öncelikli (backend `applyPackageItems` ile aynı)
  const byCode = new Map<string, DraftMetric>();
  for (const m of [...metrics].sort(
    (a, b) => Number(a.clientId !== null) - Number(b.clientId !== null),
  )) {
    if (!byCode.has(m.code)) byCode.set(m.code, m);
  }
  return { byId, kids, byCode, categories };
}

export function emptyDraft(): PackageDraft {
  return { added: [], sources: new Map(), removed: new Set(), defaults: new Map() };
}

const isGroup = (m: DraftMetric | undefined): boolean => m?.nodeKind === "breakdown_group";
const isMemberNode = (m: DraftMetric | undefined): boolean =>
  m?.dimensionMember !== null && m?.dimensionMember !== undefined;

/** Pakete girebilir mi: kategori değil, ortak katalogda, kullanımdan kalkmamış. */
export function isEligibleItem(id: string, ctx: DraftContext): boolean {
  const m = ctx.byId.get(id);
  return !!m && !ctx.categories.has(id) && m.clientId === null && m.status === "active";
}

/** "Tüm katalog" / seçici satırının pasiflik nedeni (null → etkin). */
export function itemDisabledReason(
  m: Pick<KpiMetric, "id" | "clientId" | "status">,
  ctx: DraftContext,
  selected: boolean,
): string | null {
  const C = KPI_PACKAGES_COPY;
  if (ctx.categories.has(m.id)) return C.errors.KPI_PACKAGE_CATEGORY_ITEM;
  if (m.clientId !== null) return C.errors.KPI_PACKAGE_CLIENT_REF;
  if (m.status !== "active" && !selected) return C.pills.deprecated;
  return null;
}

/** Katalog ağacı / seçici: kırılım grupları ve üyeleri satır olmaz — toplam üyeleriyle gelir. */
export function catalogTreeMetrics<T extends DraftMetric>(metrics: readonly T[]): T[] {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  return metrics.filter((m) => {
    if (isGroup(m)) return false;
    return !(isMemberNode(m) && isGroup(m.parentId ? byId.get(m.parentId) : undefined));
  });
}

export function baseMemberIds(base: readonly KpiPackageItem[]): Set<string> {
  return new Set(base.map((b) => b.metricId));
}

/** Taslaktaki üyeler — sunucu sırası (çıkarılanlar hariç), sonra eklenenler. */
export function draftMemberIds(base: readonly KpiPackageItem[], draft: PackageDraft): string[] {
  const out = base.map((b) => b.metricId).filter((id) => !draft.removed.has(id));
  const seen = new Set(out);
  for (const id of draft.added) if (!seen.has(id)) out.push(id);
  return out;
}

function withMembership(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  ids: Iterable<string>,
  on: boolean,
): PackageDraft {
  const baseIds = baseMemberIds(base);
  const added = [...draft.added];
  const sources = new Map(draft.sources);
  const removed = new Set(draft.removed);
  for (const id of ids) {
    if (on) {
      removed.delete(id);
      if (!baseIds.has(id) && !added.includes(id)) added.push(id);
    } else {
      const i = added.indexOf(id);
      if (i >= 0) added.splice(i, 1);
      sources.delete(id);
      if (baseIds.has(id)) removed.add(id);
    }
  }
  return { added, sources, removed, defaults: draft.defaults };
}

function groupsOf(id: string, ctx: DraftContext): DraftMetric[] {
  return (ctx.kids.get(id) ?? []).filter(isGroup);
}

function membersOf(groupId: string, ctx: DraftContext): DraftMetric[] {
  return (ctx.kids.get(groupId) ?? []).filter(isMemberNode);
}

/** Düğümün torun grup/üyeleri (iki seviye de) — yapısal olmayan çocuk (düz gösterge) girmez. */
export function familyBelow(id: string, ctx: DraftContext): string[] {
  const out: string[] = [];
  const stack = [id];
  const seen = new Set<string>([id]);
  while (stack.length) {
    const cur = stack.pop()!;
    for (const k of ctx.kids.get(cur) ?? []) {
      if (seen.has(k.id) || !(isGroup(k) || (isMemberNode(k) && isGroup(ctx.byId.get(cur))))) {
        continue;
      }
      seen.add(k.id);
      out.push(k.id);
      stack.push(k.id);
    }
  }
  return out;
}

/** Yapısal düğümün (grup / grup altındaki üye) aile köküne kadar atalar (köke dahil). */
function familyAbove(id: string, ctx: DraftContext): string[] {
  const out: string[] = [];
  const seen = new Set<string>([id]);
  let cur = ctx.byId.get(id);
  while (cur?.parentId) {
    const parent = ctx.byId.get(cur.parentId);
    if (!parent || seen.has(parent.id)) break;
    const structural = isGroup(cur) || (isMemberNode(cur) && isGroup(parent));
    if (!structural) break;
    seen.add(parent.id);
    out.push(parent.id);
    cur = parent;
  }
  return out;
}

/**
 * Kalemi aç/kapa (kırılım kuralıyla, iki seviyeli aile). Açarken: üye/grup → tüm ata zinciri (grup,
 * üst üye, üst grup, toplam); grup → kendi üyeleri; toplam → tüm torun grup/üyeler (iki seviye).
 * Kapatırken: kalem alt ailesiyle; son üyesi çıkan grup da çıkar (üstündeki üye korunur).
 * Uygun olmayan (kategori / müşteriye özel / kullanımdan kalkmış) kalem eklenmez.
 */
export function toggleDraftItem(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  id: string,
  on: boolean,
  ctx: DraftContext,
): PackageDraft {
  const m = ctx.byId.get(id);
  if (!m) return draft;
  const members = new Set(draftMemberIds(base, draft));
  const baseIds = baseMemberIds(base);
  // sunucuda zaten olan kalem (kullanımdan kalkmış olsa da) geri işaretlenebilir
  const ok = (x: string): boolean => members.has(x) || baseIds.has(x) || isEligibleItem(x, ctx);
  if (on) {
    if (!ok(id)) return draft;
    const ids: string[] = [id, ...familyAbove(id, ctx)];
    if (isGroup(m)) ids.push(...membersOf(id, ctx).map((x) => x.id));
    else ids.push(...familyBelow(id, ctx)); // R11-1: kendi grubu olan üye-toplam ailesini getirir
    return withMembership(base, draft, ids.filter(ok), true);
  }
  let next = withMembership(base, draft, [id, ...familyBelow(id, ctx)], false);
  const parent = m.parentId ? ctx.byId.get(m.parentId) : undefined;
  if (isMemberNode(m) && isGroup(parent)) {
    const left = new Set(draftMemberIds(base, next));
    if (!membersOf(parent!.id, ctx).some((x) => left.has(x.id))) {
      next = withMembership(base, next, [parent!.id], false);
    }
  }
  return next;
}

/** Seçici / "Tüm katalog": yeni ağaç seçimini taslağa çevirir (yalnız ağaç satırları karşılaştırılır). */
export function applyCatalogTreeSelection(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  treeIds: ReadonlySet<string>,
  next: ReadonlySet<string>,
  ctx: DraftContext,
): PackageDraft {
  const members = new Set(draftMemberIds(base, draft));
  let out = draft;
  for (const id of treeIds) {
    const want = next.has(id);
    if (want !== members.has(id)) out = toggleDraftItem(base, out, id, want, ctx);
  }
  return out;
}

/**
 * Seçicinin "Taslağa ekle": verilen kalemleri (kırılım kuralıyla) ekler. `picks` verilirse yeni
 * eklenen kalemlerin kaynağı kaydedilir: seçimin kendisi kendi kaynağını/gerekçesini, ailesinden
 * gelen grup/üye satırları seçimin kaynağını (gerekçesiz) taşır. Taban kalemlere dokunulmaz.
 */
export function addDraftItems(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  ids: Iterable<string>,
  ctx: DraftContext,
  picks?: ReadonlyMap<string, PackagePick>,
): PackageDraft {
  let out = draft;
  const baseIds = baseMemberIds(base);
  for (const id of ids) {
    const before = new Set(out.added);
    out = toggleDraftItem(base, out, id, true, ctx);
    const pick = picks?.get(id);
    if (!pick) continue;
    const sources = new Map(out.sources);
    for (const n of out.added) {
      if (before.has(n) || baseIds.has(n)) continue;
      sources.set(
        n,
        n === id
          ? { source: pick.source, reason: pick.reason }
          : { source: pick.source, reason: null },
      );
    }
    // seçim, başka bir seçimin ailesiyle önceden eklenmişse kendi kaynağı geçerli
    if (out.added.includes(id) && !baseIds.has(id)) {
      sources.set(id, { source: pick.source, reason: pick.reason });
    }
    out = { ...out, sources };
  }
  return out;
}

function baseDefaults(item: KpiPackageItem | undefined): PackageDefaults {
  return {
    unitDefault: item?.unitDefault ?? null,
    activeFormulaId: item?.activeFormulaId ?? null,
    showChange: item?.showChange ?? null,
  };
}

const sameDefaults = (a: PackageDefaults, b: PackageDefaults): boolean =>
  a.unitDefault === b.unitDefault &&
  a.activeFormulaId === b.activeFormulaId &&
  a.showChange === b.showChange;

export function effectiveDefaults(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  id: string,
  /** satır başına `find` yerine önceden kurulmuş `metricId → kalem` haritası (büyük ağaç) */
  baseById?: ReadonlyMap<string, KpiPackageItem>,
): PackageDefaults {
  return (
    draft.defaults.get(id) ??
    baseDefaults(baseById ? baseById.get(id) : base.find((b) => b.metricId === id))
  );
}

export function hasAnyDefault(d: PackageDefaults): boolean {
  return d.unitDefault !== null || d.activeFormulaId !== null || d.showChange !== null;
}

export function setDraftDefaults(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  id: string,
  patch: Partial<PackageDefaults>,
): PackageDraft {
  const next = { ...effectiveDefaults(base, draft, id), ...patch };
  const defaults = new Map(draft.defaults);
  const item = base.find((b) => b.metricId === id);
  if (item && sameDefaults(next, baseDefaults(item))) defaults.delete(id);
  else defaults.set(id, next);
  return { ...draft, defaults };
}

export interface DraftCounts {
  added: number;
  removed: number;
  /** sunucudaki üyelerin değişen varsayılanları */
  defaults: number;
}

export function draftCounts(base: readonly KpiPackageItem[], draft: PackageDraft): DraftCounts {
  const baseIds = baseMemberIds(base);
  let defaults = 0;
  for (const [id, d] of draft.defaults) {
    const item = base.find((b) => b.metricId === id);
    if (item && !draft.removed.has(id) && !sameDefaults(d, baseDefaults(item))) defaults += 1;
  }
  return {
    added: draft.added.filter((id) => !baseIds.has(id)).length,
    removed: [...draft.removed].filter((id) => baseIds.has(id)).length,
    defaults,
  };
}

export function isDraftDirty(base: readonly KpiPackageItem[], draft: PackageDraft): boolean {
  const c = draftCounts(base, draft);
  return c.added + c.removed + c.defaults > 0;
}

/** PUT gövdesi kalemleri — tam liste, varsayılanlar açık (eksik alan sunucuda null olur). */
export function draftItemsBody(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
): KpiPackageItemInput[] {
  const baseIds = baseMemberIds(base);
  return draftMemberIds(base, draft).map((metricId) => {
    const src = baseIds.has(metricId) ? undefined : draft.sources.get(metricId);
    return {
      metricId,
      ...effectiveDefaults(base, draft, metricId),
      ...(src ? { source: src.source, sourceReason: src.reason } : {}),
    };
  });
}

/**
 * 409 sonrası taslağı yeni sunucu kalemlerine yeniden uygular. `dropped` = artık uygulanamayan
 * işlemlerin gösterge kimlikleri (kullanıcıya listelenir); zaten yapılmış işlem sessizce düşer.
 */
export function rebaseDraft(
  newBase: readonly KpiPackageItem[],
  draft: PackageDraft,
  ctx: DraftContext,
): { draft: PackageDraft; dropped: string[] } {
  const newIds = baseMemberIds(newBase);
  const dropped = new Set<string>();
  const added: string[] = [];
  for (const id of draft.added) {
    if (newIds.has(id)) continue;
    if (isEligibleItem(id, ctx)) added.push(id);
    else dropped.add(id);
  }
  const removed = new Set([...draft.removed].filter((id) => newIds.has(id)));
  const defaults = new Map<string, PackageDefaults>();
  for (const [id, d] of draft.defaults) {
    const item = newBase.find((b) => b.metricId === id);
    if (item && (removed.has(id) || sameDefaults(d, baseDefaults(item)))) continue;
    if (!item && !added.includes(id)) {
      // kalem başkası tarafından paketten çıkarılmış — varsayılan düzenlemesi uygulanamaz
      dropped.add(id);
      continue;
    }
    defaults.set(id, d);
  }
  const sources = new Map<string, PackageSource>();
  for (const id of added) {
    const src = draft.sources.get(id);
    if (src) sources.set(id, src);
  }
  return { draft: { added, sources, removed, defaults }, dropped: [...dropped] };
}

/** DR20 — taslaktaki toplamların aktif ortak kırılım üyelerinden pakette olmayanlar. */
export function missingMemberIds(members: ReadonlySet<string>, ctx: DraftContext): string[] {
  const out: string[] = [];
  for (const id of members) {
    for (const g of groupsOf(id, ctx)) {
      for (const mem of membersOf(g.id, ctx)) {
        if (!members.has(mem.id) && isEligibleItem(mem.id, ctx)) out.push(mem.id);
      }
    }
  }
  return out;
}

/**
 * Kategori argümanı → torun eklenebilir yapraklar (backend `dependencyClosure.addDep` R3-1 ile
 * aynı): kırılım grubu/üyesi atlanır; yalnız KATEGORİLERİN içine inilir (toplam gösterge bir
 * yapraktır, altındaki grup/üyeler gerekli sayılmaz).
 */
function categoryLeaves(id: string, ctx: DraftContext): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (catId: string): void => {
    if (seen.has(catId)) return;
    seen.add(catId);
    for (const k of ctx.kids.get(catId) ?? []) {
      if (k.nodeKind === "breakdown_group" || (k.dimensionMember ?? null) !== null) continue;
      if (ctx.categories.has(k.id)) walk(k.id);
      else if (isEligibleItem(k.id, ctx)) out.push(k.id);
    }
  };
  walk(id);
  return out;
}

/**
 * Bağımlılık: kalem → onu formülünde kullanan taslak üyelerinin etiketleri. `TOPLA(kategori)` gibi
 * başvuru kategoriye çözülürse kategori asla "gerekli" olmaz; torun yapraklar gerekli sayılır (R3-1).
 */
export function requiredByOf(
  members: ReadonlySet<string>,
  ctx: DraftContext,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const id of members) {
    const m = ctx.byId.get(id);
    if (!m) continue;
    const refs = new Set<string>();
    for (const f of m.defaultFormulas) {
      const p = parseFormula(f.expression);
      if (p.ok) for (const r of p.refs) refs.add(r);
    }
    for (const code of refs) {
      const dep = ctx.byCode.get(code);
      if (!dep || dep.id === id) continue;
      const targets = ctx.categories.has(dep.id) ? categoryLeaves(dep.id, ctx) : [dep.id];
      for (const target of targets) {
        if (target === id) continue;
        const list = out.get(target) ?? [];
        if (!list.includes(m.label)) list.push(m.label);
        out.set(target, list);
      }
    }
  }
  return out;
}

/**
 * Kayıt isteği sürerken taslakta yapılan değişiklikler (gönderilen `sent` → güncel `latest` farkı).
 * Başarılı kayıttan sonra yalnız bu fark yeni kalemlere yeniden uygulanır; gönderilmiş işlemler
 * (sunucunun geri eklediği bağımlılığın çıkarılması dahil) taslakta kalmaz.
 */
export function draftDelta(sent: PackageDraft, latest: PackageDraft): PackageDraft {
  if (sent === latest) return emptyDraft();
  const added = [
    ...latest.added.filter((id) => !sent.added.includes(id)),
    ...[...sent.removed].filter((id) => !latest.removed.has(id)),
  ];
  const removed = new Set([
    ...[...latest.removed].filter((id) => !sent.removed.has(id)),
    ...sent.added.filter((id) => !latest.added.includes(id)),
  ]);
  const defaults = new Map<string, PackageDefaults>();
  for (const [id, d] of latest.defaults) {
    const s = sent.defaults.get(id);
    if (!s || !sameDefaults(s, d)) defaults.set(id, d);
  }
  const addedIds = [...new Set(added)];
  const sources = new Map<string, PackageSource>();
  for (const id of addedIds) {
    const src = latest.sources.get(id);
    if (src) sources.set(id, src);
  }
  return { added: addedIds, sources, removed, defaults };
}
